// Строка Projects: подпись (ручка, имя, действия) и тело (линии, бары, контуры посадки).
// Что ляжет и что записать — решает экран; здесь только отрисовка и события.
import { WriteOnly, useCanWrite } from '@core/auth/access';
import type { KeyboardEvent, PointerEvent } from 'react';
import { mondayOf, spanWeeks } from '@core/dates';
import { Menu, type MenuItem } from '@core/ui/Menu';
import { EyeOff, Pencil, Plus } from 'lucide-react';
import { BAR_HEIGHT, barGeometry, barStyle, trackTop, type Grid } from '../shared/geometry';
import { NewTaskHereMenu } from '../shared/NewTaskHere';
import { NewTaskGhost, type QuickHandlers } from '../shared/QuickTask';
import { findFreeTrack } from '../shared/planner';
import { GridLines } from '../shared/GridHeader';
import { spotProps } from '../shared/spotlight';
import { DeployFlags, dragDanger, endsAfterDeploy } from '../shared/deployGuard';
import { GhostBar, TaskBar, type BarLook, type BarMarks, type DragKind } from '../shared/TaskBar';
import { ReorderHandle, RowActions, rowAddButton } from '../shared/RowActions';
import { rowTasksNow, type RowDef, type Task } from '../shared/data';
import type { CardTab } from '../shared/cardEdits';
import type { ResizePreview, TaskDrag } from '../shared/interaction';

// Куда ляжет задача, если отпустить сейчас, — считается тем же кодом, что и запись.
export type Preview = { kind: 'move'; rowKey: string; start_date: string; end_date: string; track: number } | ResizePreview;
export type Drag = TaskDrag<boolean, Preview>; // origin: из бэклога
export type NewTaskHint = { rowKey: string; start_date: string; end_date: string; track: number };
export type QuickTask = QuickHandlers<NewTaskHint>;
/** Контур новой задачи: неделя под курсором и свободный трек строки. */
export const hintFor = (rowKey: string, date: string): NewTaskHint => { const s = spanWeeks(mondayOf(date), 1); return { rowKey, ...s, track: findFreeTrack(rowTasksNow(rowKey), s.start_date, s.end_date) }; };

type RowLabelProps = { row: RowDef; active: boolean; onHover: (projectId: string | null) => void; onAdd: () => void; onEdit: () => void; onHide: () => void; onHandle: (e: PointerEvent<HTMLElement>) => void };

// Строка проекта: ручка порядка, имя, «+» задача (частое действие); правка и скрытие — в меню по правой кнопке.
// Подпроект — только имя и «+». «+» показывается у всего блока проекта, пока курсор над любой его строкой.
export function RowLabel({ row, active, onHover, onAdd, onEdit, onHide, onHandle }: RowLabelProps) {
  const canWrite = useCanWrite();
  const menu: MenuItem[] = canWrite
    ? [
      ...(row.header ? [] : [{ label: 'New task', onSelect: onAdd, icon: Plus }]),
      ...(row.isSub ? [] : [{ label: 'Edit project', onSelect: onEdit, icon: Pencil }, { label: 'Hide (only for you)', onSelect: onHide, icon: EyeOff }]),
    ]
    : row.isSub ? [] : [{ label: 'Hide (only for you)', onSelect: onHide, icon: EyeOff }];
  return (
    <Menu items={menu}>
    <div data-project-label={row.isSub ? undefined : row.project_id} data-project-header={row.header ? '' : undefined} className={`relative h-full flex items-center gap-1 py-1 ${row.isSub ? 'pl-6 pr-2 text-sm text-gray-600' : 'px-1 font-bold'}`}
      style={row.header ? { color: row.color } : undefined} onPointerEnter={() => onHover(row.project_id)} onPointerLeave={() => onHover(null)}>
      {!row.isSub && <WriteOnly><ReorderHandle label={row.label} show={active} onPointerDown={onHandle} /></WriteOnly>}
      {!row.isSub && <span className="inline-block w-3 h-3 rounded-full shrink-0 mr-1" style={{ background: row.color }} aria-hidden data-project-color />}
      <span className="truncate" data-project-name title={row.label}>{row.label}</span>
      {!row.header && (
        <RowActions bg={row.isSub ? 'bg-gray-50' : 'bg-gray-100'} show={active}>
          <WriteOnly><button type="button" className={rowAddButton} onClick={onAdd} aria-label={`Add task to ${row.label}`} title="Add task">+</button></WriteOnly>
        </RowActions>
      )}
    </div>
    </Menu>
  );
}

type RowGridProps = {
  row: RowDef; tasks: Task[]; slots: number[]; tracks: number; grid: Grid; todayX: number | null;
  marks: BarMarks; look: BarLook; drag: Drag | null; groupShift: { ids: Set<string>; days: number } | null;
  hint: NewTaskHint | null; onHint: (h: NewTaskHint | null) => void;
  quick: QuickTask | null; onQuickAt: (h: NewTaskHint) => void;
  onBarPointerDown: (task: Task, fromBacklog: boolean, kind: DragKind, e: PointerEvent<HTMLElement>) => void;
  onOpen: (t: Task, tab?: CardTab) => void; onKey: (t: Task, ctx: null, e: KeyboardEvent<HTMLElement>) => void; menuFor: (t: Task) => MenuItem[];
  onNewAt: (date: string) => void;
};

export function RowGrid({ row, tasks, slots, tracks, grid, todayX, marks, look, drag, groupShift, hint, onHint, quick, onQuickAt, onBarPointerDown, onOpen, onKey, menuFor, onNewAt }: RowGridProps) {
  const slotOf = (track: number) => Math.max(0, slots.indexOf(track));
  const { barH } = look;
  return (
    <>
      <GridLines grid={grid} todayX={todayX} days={look.days} holidays={look.holidays} spot={look.spotBox} />
      {/* контур новой задачи — неделя под курсором и свободный трек строки; в заголовке проекта задач не заводят */}
      {!row.header && <NewTaskHereMenu grid={grid} onHint={onHint} onNewAt={onNewAt} onQuickAt={onQuickAt} hintOf={(date) => hintFor(row.key, date)} />}
      {!row.header && <NewTaskGhost hint={hint} quick={quick} grid={grid} place={(t) => { const idx = slots.indexOf(t.track); const h = tasks.length ? barH : BAR_HEIGHT; return { top: trackTop(idx >= 0 ? idx : slots.length, h), height: h }; }} />}
      <DeployFlags tasks={tasks} drag={drag} grid={grid} height={barH} topOf={(t) => trackTop(slotOf(t.track ?? 0), barH)} />
      {tasks.map((t) => {
        const box = barGeometry(t, grid);
        if (!box) return null;
        const isDragged = drag?.task.id === t.id;
        const shifted = !isDragged && !!groupShift?.ids.has(t.id);
        // растяжение показывается уже привязанным к неделям — как ляжет
        const style = barStyle(box, { dragged: isDragged, kind: drag?.kind, dx: drag?.dx, previewBox: drag?.preview?.kind === 'resize' ? barGeometry(drag.preview, grid) : null, shiftDays: shifted ? groupShift!.days : null });
        return (
          <TaskBar
            key={t.id} task={t} box={{ ...box, ...style }} top={trackTop(slotOf(t.track ?? 0), barH)} height={barH} wrap={look.wrap}
            colors={marks.colors.get(t.id) ?? []} names={marks.names.get(t.id) ?? []}
            attention={marks.attention.get(t.id)} pending={marks.pendingIds.has(t.id)}
            ghost={(isDragged && drag?.kind === 'move') || shifted} selected={marks.selected.has(t.id)}
            {...spotProps(t, look.spot)}
            danger={dragDanger(t, isDragged, drag, shifted, groupShift)}
            onPointerDown={(kind, e) => onBarPointerDown(t, false, kind, e)}
            onOpen={() => onOpen(t)} onOpenQuestions={() => onOpen(t, 'questions')} onKey={(e) => onKey(t, null, e)} menu={menuFor(t)} questions={marks.questions.get(t.id)} badges={look.badges} releaseMarks={look.releaseMarks}
          />
        );
      })}
      {drag?.preview?.kind === 'move' && drag.preview.rowKey === row.key && (() => {
        const p = drag.preview;
        const g = barGeometry(p, grid);
        if (!g) return null;
        const idx = slots.indexOf(p.track);
        return <GhostBar left={g.left} width={g.width} top={trackTop(idx >= 0 ? idx : tracks - 1, barH)} height={barH} danger={endsAfterDeploy(drag.task, p.end_date)}>{drag.task.title}</GhostBar>;
      })()}
    </>
  );
}
